"use client"
import React, {useMemo, useState} from "react";
import {Box, Skeleton, Typography} from "@mui/material";
import FaIcon from "@/components/utilities/fa-icon";
import FaqItem from "./faq-item";
import {useAppSelector} from "@/redux/hooks";
import {RootState} from "@/redux/store";
import CreateEditModal from "./faq-create-edit-modal";
import Faq, {FaqCategoryName} from "@/types/faq";
import Container from "@mui/material/Container";
import Button from "@mui/material/Button";
import {useTheme} from "@mui/material/styles";
import {useFaqs} from "@/lib/api";


export default function FaqView() {
    const {data: faqs} = useFaqs();
    const [expandedAccordion, setExpandedAccordion] = React.useState<string | null | number>(1);
    const theme = useTheme()
    const [faqToEdit, setFaqToEdit] = useState<Faq | undefined>(undefined);

    const [visibleModal, setVisibleModal] = useState<boolean>(false);
    const isAdmin = useAppSelector((state: RootState) => state.user.currentUser?.roles?.includes("Admin"));

    const categoryKeys = useMemo(() => {
        return Object.keys(FaqCategoryName).filter((k) => !Number.isNaN(Number(k)));
    }, []);

    const faqsByCategory = useMemo(() => {
        const map = new Map<number, Faq[]>();
        if (!faqs) return map;

        for (const f of faqs) {
            const key = Number(f.category);
            const arr = map.get(key) ?? [];
            arr.push(f);
            map.set(key, arr);
        }
        return map;
    }, [faqs]);


    const handleChange = (accordionName: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedAccordion(isExpanded ? accordionName : null);
    };

    const handleEdit = (faq: Faq) => {
        setFaqToEdit(faq);
        setVisibleModal(true);
    };

    return (

        <Box minHeight = '100vh' className = "cog-background" >
            <Container maxWidth = 'lg' sx = {{py: 6}} >

                <Box
                    display = "flex"
                    alignItems = "center"
                    flexDirection = "column"
                    mb = {3}
                >
                    <Typography fontSize = {20} mb = {2} textAlign = "left" fontWeight = {400} >
                        سوالات متداول
                    </Typography >
                    <FaIcon fontSize = {24} color = {theme.palette.primary.main} faIcon = 'fa-arrow-down' />

                </Box >

                {
                    isAdmin &&
                    <Box display = "flex" justifyContent = "flex-end" >
                      <Button
                          onClick = {() => {
                              setFaqToEdit(undefined)
                              setVisibleModal(true)
                          }}
                          sx = {{mb: 3}}
                          variant = "contained"
                          startIcon = {<FaIcon fontSize = {18} faIcon = 'fa-plus' />} >
                        افزودن سوال جدید
                      </Button >

                      <CreateEditModal
                          visible = {visibleModal}
                          onClose = {() => {
                              setVisibleModal(false)
                              setFaqToEdit(undefined)
                          }}
                          dataToEdit = {faqToEdit}
                      />
                    </Box >
                }

                <Box width = "100%" >
                    {
                        faqs ?
                            categoryKeys.map((categoryKey) => {
                                    const categoryNumber = Number(categoryKey);
                                    const label = FaqCategoryName[categoryNumber as unknown as keyof typeof FaqCategoryName];
                                    const items = faqsByCategory.get(categoryNumber) ?? [];

                                    if (items.length === 0) return null;

                                    return (
                                        <Box key = {categoryKey} mb={6} >
                                            <Typography
                                                fontSize = {20}
                                                fontWeight = {400}
                                                color = 'primary'
                                                marginBottom = {2}
                                            >
                                                {label}
                                            </Typography >

                                            {items.map((faq) => {
                                                const idStr = String(faq.id);

                                                return (
                                                    <FaqItem
                                                        key = {idStr}
                                                        handleEdit = {handleEdit}
                                                        id = {faq.id}
                                                        question = {faq.question}
                                                        category = {faq.category}
                                                        answer = {faq.answer}
                                                        expandedAccordion = {expandedAccordion === idStr}
                                                        handleChange = {handleChange(idStr)}
                                                    />
                                                );
                                            })}

                                        </Box >
                                    )
                                }
                            )
                            :
                            <>
                                {categoryKeys.map((categoryKey) => {
                                    const categoryNumber = Number(categoryKey);
                                    const label = FaqCategoryName[categoryNumber as unknown as keyof typeof FaqCategoryName];

                                    return (
                                        <Box key = {categoryKey} mb={5} >
                                            <Typography fontSize = {20} fontWeight = {400} color = "primary" mb = {2} >
                                                {label}
                                            </Typography >

                                            {Array.from({length: 5}).map((_, i) => (
                                                <Box display = "flex" mb = {2} >
                                                    <Skeleton
                                                        key = {`${categoryKey}-${i}`}
                                                        variant = "rectangular"
                                                        width = "100%"
                                                        height = {47}
                                                    />
                                                </Box >
                                            ))}
                                        </Box >
                                    );
                                })}
                            </>
                    }
                </Box >

            </Container >

        </Box >


    )
}
